import React, {useState, useEffect, useRef} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {Grid, Box, useTheme} from '@mui/material';
import TextInput from '../../common/TextInput';
import FormWrapper from '../../common/StyledFormWrapper';
import CheckBoxInput from '../../common/CheckBoxInput';
import {Link, useSearchParams} from 'react-router-dom';
import Alert from '@mui/material/Alert';
import useDebounce from '../../../hooks/useDebounce';
import {CONSTANTS} from '../../../constants';
import {loginUserApi} from '../../../api';
import {validateEmail} from '../../../utils';
import sdlogo from '../../../images/sdlogo.svg';
import useAuthStore from '../../../store/authStore';
import useTranslations from '../../../hooks/useTranslation';
import usePostHogHook from '../../../hooks/usePosthogHook';
import {updateUserPreferedLangApi} from '../../../api';


const Login = () => {
  const theme = useTheme();
  const iconColor = theme.palette.icon.primary;
  const {translate} = useTranslations();
  const {loginUser, setRememberEmail, rememberEmail, setOrgToTheme, setTheme, setOrganization, updateUserPreferedLang, is_admin_login_mobile} = useAuthStore(state => state);
  const [email, setEmail] = useState(rememberEmail || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isRemember, setIsRemember] = useState(rememberEmail ? true : false);
  const [loading, setLoading] = useState(false);
  const [showAlreadySignedUpMessage, setShowAlreadySignedUpMessage] = useState(false);
  const hasCheckedSignUpStatus = useRef(false);
  const debounceEmail = useDebounce(email, CONSTANTS.DEBOUNCE_TIMEOUT);
  const {identifyUser} = usePostHogHook();

  const handleLogin = async e => {
    e.preventDefault();
    if (!email || !password) {
      setError(translate(CONSTANTS.MESSAGES.ALL_FIELDS_REQUIRED));
      return;
    }
    if (!validateEmail(email)) {
      return;
    }
    if (password?.length < CONSTANTS.PASSWORD_MAX_LENGTH) {
      setError(translate(CONSTANTS.MESSAGES.INVALID_CRED));
      return;
    }
    try {
      const data = {email: email.toLowerCase().trim(), password};
      setLoading(true);
      setError(null);
      const response = await loginUserApi(data);
      const responseData = response?.data;
      const responseErrorMessage = response?.response?.data?.message || '';

      if (responseData?.status === 200) {
        const data = responseData?.data;
        const organizations = data?.organizations || [];
        
        const getOrgStatus = (orgId) => {
          const uo = (data?.user_organizations || []).find(uo => String(uo.organization_id) === String(orgId));
          return (uo?.status || '').toUpperCase();
        };

        const activeOrganization = organizations.find(org => Number(org?.deactivate_login) !== 1 && !['MOVED', 'DROPPED', 'ARCHIVED', 'DELETED', 'INVITED'].includes(getOrgStatus(org.id)));
        const roleRequiresOrganization = ['ADMIN', 'LEADER', 'DISCIPLE'].includes(data?.user_role);

        if (roleRequiresOrganization && organizations.length === 0) {
          useAuthStore.getState().openOrgLoginDisabledModal({action: 'dismiss'});
          setLoading(false);
          return;
        }
        
        // Store CSRF token from response if provided
        if (response.data.csrfToken) {
          localStorage.setItem('csrfToken', response.data.csrfToken);
        }

        // Carry a language chosen ON THIS SCREEN into the account — but ONLY if the visitor
        // actually chose one (SIAA-545).
        //
        // This used to run unconditionally off `selectedLang`, which is just the language the
        // browser happens to be showing, not a decision. So every login rewrote the account's
        // preference to the login screen's language: an admin who set Spanish, signed out and
        // signed back in on an English-defaulted browser had their Spanish silently destroyed —
        // once here in the user object and again by the PATCH below, so it did not survive a
        // refresh either.
        //
        // LanguageSwitcher sets `langChosenPreLogin` only when a signed-out visitor clicks a flag,
        // which is the one moment the choice is unambiguous. Absent that, the server's stored
        // preference wins and `data.user_prefered_lang` is left exactly as it arrived.
        const storedLang = localStorage.getItem('selectedLang');
        const preLoginChoice = localStorage.getItem('langChosenPreLogin');
        localStorage.removeItem('langChosenPreLogin');

        let currentLangKey = null;
        if (preLoginChoice) {
          const chosen = parseInt(preLoginChoice, 10);
          if (CONSTANTS.LANG[chosen]) currentLangKey = chosen;
        }
        if (currentLangKey) {
          data.user_prefered_lang = currentLangKey;
        }

        const org_to_theme = data?.org_to_theme;
        setOrgToTheme(org_to_theme);
        const orgs = data?.organizations?.[0]?.id;
        const defaultThemeId = org_to_theme?.filter(org => {
          return org.org_id === orgs;
        })[0]?.theme_id;
        if (defaultThemeId) {
          setTheme(defaultThemeId);
        }
        // Prioritize finding an organization that actually supports the user's assigned global role
        let roleSupportedOrg = null;
        if (data.user_organizations && Array.isArray(data.user_organizations)) {
          const orgWithRole = data.user_organizations.find(uo => uo.user_role?.toUpperCase() === data.user_role?.toUpperCase() && !['MOVED', 'DROPPED', 'ARCHIVED', 'DELETED', 'INVITED'].includes((uo.status || '').toUpperCase()));
          if (orgWithRole) {
            roleSupportedOrg = organizations.find(org => String(org.id) === String(orgWithRole.organization_id) && Number(org?.deactivate_login) !== 1);
          }
        }

        const targetOrgIdFromUrl = searchParams.get('orgId') || searchParams.get('targetOrgId');
        const targetOrgObj = targetOrgIdFromUrl ? organizations.find(o => String(o.id) === String(targetOrgIdFromUrl)) : null;
        
        // Use targetOrgObj (if valid), else the org supporting the role, else fallback to activeOrganization or pendingInviteOrg
        const activeOrgFromBackend = data?.active_org_id ? organizations.find(org => String(org.id) === String(data.active_org_id) && Number(org?.deactivate_login) !== 1) : null;
        const pendingInviteOrg = data?.pending_move_invite?.orgId ? organizations.find(org => String(org.id) === String(data.pending_move_invite.orgId) && Number(org?.deactivate_login) !== 1) : null;
        const defaultOrganization = targetOrgObj || roleSupportedOrg || activeOrgFromBackend || activeOrganization || pendingInviteOrg || null;
        
        if (roleRequiresOrganization && !defaultOrganization && !data?.pending_move_invite) {
          useAuthStore.getState().openOrgLoginDisabledModal({action: 'dismiss'});
          setLoading(false);
          return;
        }
        
        let userToLogin = { ...data };
        if (defaultOrganization && userToLogin.user_organizations && Array.isArray(userToLogin.user_organizations)) {
          const targetOrgs = userToLogin.user_organizations.filter(
            uo => String(uo.organization_id) === String(defaultOrganization.id) && uo.status !== 'DELETED' && uo.status !== 'INVITED'
          );
          const availableRoles = targetOrgs.map(uo => uo.user_role);
          if (availableRoles.length > 0 && !availableRoles.includes(userToLogin.user_role)) {
            const fallbackRole = availableRoles.includes('DISCIPLE') ? 'DISCIPLE' : (availableRoles.includes('LEADER') ? 'LEADER' : availableRoles[0]);
            userToLogin.user_role = fallbackRole;
          }
        }
        if (defaultOrganization) {
          setOrganization(defaultOrganization);
        }
        
        identifyUser(userToLogin);
        loginUser(userToLogin);

        // Only persist a language the visitor picked on this screen. Without the guard this
        // overwrote the account's saved preference on every single login.
        if (data?.id && currentLangKey) {
          try {
            await updateUserPreferedLangApi(data.id, currentLangKey);
          } catch(err) {}
        }

        // Reload so translations apply perfectly when the language actually changes. Compare
        // against whichever language now wins — the pre-login choice if there was one, otherwise
        // the account's own preference, which is what App.js is about to apply.
        const effectiveLangKey = currentLangKey || data?.user_prefered_lang;
        const newLangValue = Object.values(CONSTANTS.LANG).find(l => l.key === effectiveLangKey)?.value;
        if (newLangValue && storedLang && storedLang !== newLangValue) {
          setTimeout(() => { window.location.reload(); }, 100);
        }
        // If every organization has logins disabled, still complete login; App shows modal then signs out on OK.
      }
      if (responseData?.status !== 200 && /deactiv|disabled/i.test(responseErrorMessage)) {
        useAuthStore.getState().openOrgLoginDisabledModal({action: 'dismiss'});
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (/deactiv|disabled/i.test(error?.response?.data?.message || '')) {
        useAuthStore.getState().openOrgLoginDisabledModal({action: 'dismiss'});
      }
    }
  };

  const clearError = () => {
    setError(null);
  };

  const setRememberHandler = () => {
    setIsRemember(true);
    if (validateEmail(debounceEmail)) {
      setRememberEmail(debounceEmail);
      localStorage.setItem('savedPassword', password);
      localStorage.setItem('savedEmail', email);
    }
  };

  const resetRememberHandler = () => {
    setIsRemember(false);
    localStorage.removeItem('savedPassword');
    localStorage.removeItem('savedEmail');
  };

  useEffect(() => {
    const savedPassword = localStorage.getItem('savedPassword');
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedPassword) {
      setPassword(savedPassword);
    }
    if (savedEmail) {
      setEmail(savedEmail);
      setIsRemember(true);
    }
  }, []);

  useEffect(() => {
    if (!hasCheckedSignUpStatus.current) {
      const alreadySignedUp = searchParams.get('alreadySignedUp');
      const action = searchParams.get('action');
      if (alreadySignedUp === 'true' || action === 'move_disciple' || action === 'invite') {
        setShowAlreadySignedUpMessage(true);
        if (alreadySignedUp === 'true') {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('alreadySignedUp');
          setSearchParams(newSearchParams, { replace: true });
        }
      }
      const orgDisabled = searchParams.get('orgDisabled');
      if (orgDisabled === 'true') {
        useAuthStore.getState().openOrgLoginDisabledModal({action: 'dismiss'});
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('orgDisabled');
        setSearchParams(newSearchParams, {replace: true});
      }
      hasCheckedSignUpStatus.current = true;
    }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <CssBaseline />
      <Box mt={1} px={3} overflow="auto">
        <Box display="flex" justifyContent="center" mb={1}>
          <img src={sdlogo} width="100" height="100" />
        </Box>
        <Box
          sx={{
            width: '100%',
            maxWidth: {xs: '100%', sm: 412},
            marginInline: 'auto'
          }}
        >
        <FormWrapper
          subHeadingText={{
            text: (
              <>
                {translate('New?')} <span style={{textDecoration: 'underline'}}>{translate('Learn More')}</span> 
              </>
            ),
            onClick: () => {
              window.open(
                CONSTANTS.GET_STARTED_LINKS.TWO,
                '_blank',
                'noopener,noreferrer'
              );
            }
            // text: (
            //   <>
            //     {translate('New?')} <span style={{textDecoration: 'underline'}}>{translate('Create an account')}</span> 
            //   </>
            // ),
            // onClick: () => navigate('/signup')
          }}
          buttonInputs={[{text: translate('Log In'), onClick: handleLogin, type: 'submit'}]}
          headingText={translate('Log In')}
          loading={loading}
          disabled={loading}
        >
          <Grid overflow="hidden" container mt={2}>
            <Grid item xs={12} marginBottom={2}>
              <TextInput
                value={email}
                autoComplete="off"
                autoFocus={true}
                placeholder={translate('Email')}
                label={translate('Email')}
                onChange={e => {
                  setEmail(String(e.target.value).replace(/\s+/g, ''));
                  clearError();
                  resetRememberHandler();
                }}
                isAllowSpecialChar={true}
                error={debounceEmail && !validateEmail(debounceEmail)}
                helperText={debounceEmail && !validateEmail(debounceEmail) ? translate('Please enter a valid email') : ''}
                labelVariant="body2"
              />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <div className="relative_container">
                <TextInput
                  value={password}
                  autoComplete="new-password"
                  onChange={e => {
                    setPassword(e.target.value);
                    clearError();
                  }}
                  isAllowSpecialChar={true}
                  placeholder={translate('Password')}
                  type={showPassword ? 'text' : 'password'}
                  label={translate('Password')}
                  labelVariant="body2"
                  // error={debouncePassword && debouncePassword.length < 8}
                  // helperText={debouncePassword && debouncePassword.length < 8 ? "Password length should be minimum 8 characters" : ""}
                />
                {showPassword ? <VisibilityIcon onClick={() => setShowPassword(false)} className="eye_icon" /> : <VisibilityOffIcon onClick={() => setShowPassword(true)} className="eye_icon" />}
              </div>
              <div className="reset-link" color={iconColor} style={{textAlign: 'right', marginTop: '8px'}}>
                <Link className="navigation-links" to="/forgot-password">
                  {translate('Forgot Password')}
                </Link>
              </div>
            </Grid>
            <Grid item xs={12} sx={{height: '55px'}}>
              {error && <Alert severity="error">{error}</Alert>}
            </Grid>
            {showAlreadySignedUpMessage && (
              <Grid item xs={12} marginBottom={2}>
                <Alert severity="info" onClose={() => setShowAlreadySignedUpMessage(false)}>
                  {translate('You have an account or a pending invitation. Please log in with your credentials to continue.')}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <CheckBoxInput checked={isRemember} onChange={() => (isRemember ? resetRememberHandler() : setRememberHandler())} label={translate('Remember Login')} />
            </Grid>
          </Grid>
        </FormWrapper>
        </Box>
      </Box>
    </>
  );
};

export default Login;
